import { useEffect, useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { animated, useTransition } from 'react-spring';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { DisplayS, LabelL } from '@sorare/core/src/atoms/typography';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { glossary } from '@sorare/core/src/lib/glossary';
import {
  largeDesktopAndAbove,
  mediumMobileHeightAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { useNBAEvents } from '@sorare/gameplay/src/lib/events';

import { CardsLayout } from 'components/lineup/CardsLayout';

import boxImage from './assets/boxImage.png';
import chasePointsImage from './assets/chasePointsImage.png';

const mainMessages = {
  0: defineMessages({
    title: {
      id: 'S3Onboarding.Welcome.title',
      defaultMessage: 'Welcome {nickname}',
    },
    subtitle: {
      id: 'S3Onboarding.Welcome.subtitle',
      defaultMessage:
        'Collect official digital NBA player cards and compete in fantasy tournaments.',
    },
  }),
  1: defineMessages({
    title: {
      id: 'S3Onboarding.chase.title',
      defaultMessage: 'Chase Glory',
    },
    subtitle: {
      id: 'S3Onboarding.chase.climbGlobalLeaderboards',
      defaultMessage:
        'Climb global leaderboards to win cash, cards, VIP experiences and more.',
    },
  }),
};

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  flex-direction: column;
  isolation: isolate;
  justify-content: space-between;

  @media ${tabletAndAbove} {
    gap: var(--double-unit);
  }

  & > * {
    z-index: 1;
    position: relative;
  }
`;

const Content = styled(Vertical)`
  width: 100%;
  flex: 1;
`;

const Padded = styled.div`
  padding: 0 var(--double-unit);
  flex: 1;
  display: flex;
`;

const ButtonWrapper = styled.div`
  width: 100%;
  margin-top: auto;
  padding: var(--double-unit);
  display: flex;
  gap: var(--unit);
`;

const StyledButton = styled(Button)`
  flex: 1;
`;

const TextContent = styled(Vertical)`
  height: 80px;
  display: flex;
  align-items: center;
  position: relative;

  @media ${mediumMobileHeightAndAbove} {
    margin-top: var(--triple-unit);
  }

  & > * {
    padding-inline: var(--double-unit);
  }
`;

const ImageWrapper = styled.div`
  position: relative;
`;

const CardImg = styled.img`
  aspect-ratio: var(--card-aspect-ratio);
  pointer-events: none;
  user-select: none;
  max-width: 100%;
  width: 100%;
  min-width: 0;
`;

const Img = styled(animated.img)`
  margin: 0 auto;
  max-width: 240px;
  pointer-events: none;
  user-select: none;

  @media ${mediumMobileHeightAndAbove} {
    max-width: calc(100% - var(--quadruple-unit) * 2);
  }

  @media ${tabletAndAbove} {
    max-width: calc(100% - var(--quadruple-unit) * 5);
  }

  @media ${largeDesktopAndAbove} {
    max-width: calc(100% - var(--quadruple-unit) * 2);
  }
`;

const GradientOverlay = styled.div`
  --height: 42px;

  position: absolute;
  left: 0;
  right: 0;
  opacity: 0.3;
  filter: blur(7px);
  pointer-events: none;
  height: var(--height);
  bottom: calc(var(--height) * -1);
  background: linear-gradient(180deg, #ffa500 0%, rgba(253, 187, 72, 0) 100%);
`;

const SpaceAroundVertical = styled(Vertical)`
  flex: 1;
  justify-content: space-around;
`;

type Props = {
  step: 0 | 1;
  setStep: (step: number) => void;
};

export const Welcome = ({ step, setStep }: Props) => {
  const track = useNBAEvents();
  const { currentUser } = useCurrentUserContext();
  const [direction, setDirection] = useState<'next' | 'back'>('next');

  useEffect(() => {
    if (step === 0) track('View Onboarding Welcome');
    if (step === 1) track('View Onboarding Chase Glory');
  }, [step, track]);

  const transitions = useTransition(step, {
    from: {
      opacity: 0,
      transform:
        direction === 'next' ? 'translateX(100%)' : 'translateX(-100%)',
    },
    enter: { opacity: 1, transform: 'translateX(0%)' },
    leave: {
      opacity: 0,
      transform:
        direction === 'next' ? 'translateX(-100%)' : 'translateX(100%)',
    },
    config: { tension: 200, friction: 20 },
  });

  const imageTransition = useTransition(step === 1, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { tension: 200, friction: 20 },
  });

  const images = [
    'https://assets.sorare.dev/image-resize/card/d9ce25ba-8922-4bc2-9b81-70bd6cf8b1bc/picture/tinified-bc0766d8ff518f373299d5d29c251df0.png?width=320',
    'https://assets.sorare.dev/image-resize/card/55349894-02d3-47de-b142-1d85bbe77df9/picture/tinified-bc77d86ab9223d452274054d12fe7563.png?width=320',
    'https://assets.sorare.dev/image-resize/card/a8ad4571-2ded-447e-b2bc-373422dea8af/picture/tinified-e3e4d54a16720e7ca240190d26bf06fb.png?width=320',
    'https://assets.sorare.dev/image-resize/card/60795821-b04b-4c01-94bf-1533d3753cf7/picture/tinified-ee5cf4ca3516bc0e1af39d4c143eb072.png?width=320',
    'https://assets.sorare.dev/image-resize/card/200572d8-5706-421b-8dd3-18ddf10eaf11/picture/tinified-649ace575961d9d4caadcd749b85a5f4.png?width=320',
  ];

  if (!currentUser) return null;

  return (
    <Wrapper>
      <Content>
        <TextContent gap={0.5}>
          {transitions((style, item) => (
            <animated.div
              key={item}
              style={{ ...style, position: 'absolute', width: '100%' }}
            >
              <DisplayS className="text-center">
                <FormattedMessage
                  {...mainMessages[item].title}
                  values={{ nickname: currentUser?.nickname }}
                />
              </DisplayS>
              <LabelL color="var(--c-neutral-700)" className="text-center">
                <FormattedMessage {...mainMessages[item].subtitle} />
              </LabelL>
            </animated.div>
          ))}
        </TextContent>
        {step !== 1 ? (
          <Padded>
            <CardsLayout fillHeightPercent={0.6}>
              {images.map(src => (
                <ImageWrapper key={src + step}>
                  <CardImg src={src} alt="" />
                  <GradientOverlay />
                </ImageWrapper>
              ))}
            </CardsLayout>
          </Padded>
        ) : (
          imageTransition(
            (style, item) =>
              item && (
                <SpaceAroundVertical gap={4}>
                  <Img
                    style={style}
                    src={boxImage}
                    alt=""
                    className="boxImage"
                  />
                  <Img style={style} src={chasePointsImage} alt="" />
                </SpaceAroundVertical>
              )
          )
        )}
      </Content>
      <ButtonWrapper>
        {step > 0 && (
          <StyledButton
            fullWidth
            size="large"
            color="quaternary"
            disableDebounce
            onClick={() => {
              setDirection('back');
              setStep(step - 1);
            }}
          >
            <FormattedMessage {...glossary.back} />
          </StyledButton>
        )}
        <StyledButton
          fullWidth
          size="large"
          color="secondary"
          disableDebounce
          onClick={() => {
            setDirection('next');
            setStep(step + 1);
          }}
        >
          <FormattedMessage {...glossary.next} />
        </StyledButton>
      </ButtonWrapper>
    </Wrapper>
  );
};
